import React from "react";
import styled from "styled-components";

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
`;

const NumbersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 86px);
  grid-auto-rows: 86px;
  gap: 10px;
`;

const DigitKey = styled.button`
  width: 86px;
  height: 86px;
  border-radius: 8px;
  background-color: var(--color-white);
  color: var(--color-black);
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 300;
  font-size: 48px;
  line-height: 150%;
  cursor: pointer;
  transition: 0.1s;
  font-family: "Rubik", sans-serif;
  box-shadow: 3px 7px 11px rgba(0, 0, 0, 0.25);
  &:active {
    opacity: 0.1;
  }
`;

const EmptyKey = styled.div`
  width: 86px;
  height: 86px;
  border-radius: 8px;
  background-color: var(--color-white);
  box-shadow: 3px 7px 11px rgba(0, 0, 0, 0.25);
`;

const ActionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BackspaceKey = styled.button`
  width: 86px;
  height: 86px;
  border-radius: 8px;
  background-color: var(--color-danger);
  color: var(--color-white);
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: 0.1s;
  box-shadow: 3px 7px 11px rgba(0, 0, 0, 0.25);
  &:active {
    opacity: 0.75;
  }
`;

const EnterKey = styled.button`
  width: 86px;
  height: 279px;
  border-radius: 8px;
  background: radial-gradient(circle at center, #fd9c2d 0%, #ff8800 80%);
  color: var(--color-white);
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  font-size: 20px;
  border: none;
  cursor: pointer;
  transition: 0.1s;
  box-shadow: 3px 7px 11px rgba(0, 0, 0, 0.25);
  &:active {
    opacity: 0.9;
  }
  &:disabled {
    background: var(--color-grey65);
    cursor: not-allowed;
    opacity: 1;
  }
`;

const ArrowIcon = ({ color = "white", width = 24, height = 18 }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 39 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.791924 13.6248C0.0108757 14.4059 0.0108757 15.6722 0.791924 16.4533L13.5198 29.1812C14.3009 29.9622 15.5672 29.9622 16.3483 29.1812C17.1293 28.4001 17.1293 27.1338 16.3483 26.3528L5.03456 15.0391L16.3483 3.72535C17.1293 2.94431 17.1293 1.67798 16.3483 0.896927C15.5672 0.115878 14.3009 0.115878 13.5198 0.896927L0.791924 13.6248ZM38.5195 13.0391L2.20614 13.0391V17.0391L38.5195 17.0391V13.0391Z"
      fill={color}
    />
  </svg>
);

const Keyboard = ({ value, onChange, onEnter, maxLength = 8 }) => {
  const append = (digit) => {
    if (String(value || "").length >= maxLength) return;
    onChange(String(value || "") + String(digit));
  };

  const backspace = () => {
    const v = String(value || "");
    onChange(v.slice(0, -1));
  };

  const layout = [7, 8, 9, 4, 5, 6, 1, 2, 3, null, 0, null];
  const canEnter = String(value || "").length >= 7;

  return (
    <Wrapper role="application" aria-label="Teclado numérico">
      <NumbersGrid>
        {layout.map((item, idx) =>
          item === null ? (
            <EmptyKey key={idx} />
          ) : (
            <DigitKey
              key={idx}
              onClick={() => append(item)}
              aria-label={`Número ${item}`}
            >
              {item}
            </DigitKey>
          )
        )}
      </NumbersGrid>
      <ActionsColumn>
        <BackspaceKey onClick={backspace} aria-label="Borrar">
          <ArrowIcon color="white" width={26} height={20} />
        </BackspaceKey>
        <EnterKey
          disabled={!canEnter}
          onClick={() => canEnter && onEnter()}
          aria-label="Ingresá"
        >
          <span
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              lineHeight: 1.05,
              letterSpacing: 1,
              gap: 5,
            }}
          >
            <span>I</span>
            <span>N</span>
            <span>G</span>
            <span>R</span>
            <span>E</span>
            <span>S</span>
            <span>Á</span>
          </span>
        </EnterKey>
      </ActionsColumn>
    </Wrapper>
  );
};

export default Keyboard;
